import { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useRedline } from '@libs/redline';
import { pageNames, pagePaths, privatePagePaths, publicPagePaths } from '@common/routes/pageNames';
import PublicRoutes from '@common/routes/PublicRoutes';
import PrivateRoutes from '@common/routes/PrivateRoutes';
import PageLoader from '@common/components/PageLoader';
import useTitle from '@common/hooks/useTitle';
import useDatadog from '@libs/utils/hooks/datadog/useDatadog';
import { Router as useAdmissionRouter } from '@admission/routes/Router';
import { Router as useDuesRouter } from '@dues/routes/Router';
import { Router as useTallyRouter } from '@tally/routes/Router';
import { Router as useHubRouter } from '@hub/routes/Router';
import { Conditional } from '@consumidor-positivo/aurora';

const NotFound = lazy(() => import('@common/pages/NotFound'));
const Account = lazy(() => import('@admission/pages/Account'));
const DeleteAccountOngoing = lazy(() => import('@libs/acp/pages/Account/DeleteAccountOngoing'));
const UpdateEmailACP = lazy(() => import('@admission/pages/ACP/UpdateEmail'));
const UpdatePhoneACP = lazy(() => import('@admission/pages/ACP/UpdatePhone'));
const UpdateSocialNameACP = lazy(() => import('@admission/pages/ACP/UpdateSocialName'));

const UpdateContactSuccessACP = lazy(() => import('@admission/pages/ACP/Success/UpdateContactSuccess'));
const UpdatePasswordACP = lazy(() => import('@admission/pages/ACP/UpdatePassword'));
const UpdatePasswordSuccessACP = lazy(() => import('@admission/pages/ACP/Success/UpdatePasswordSuccess'));
const ACPSignInUnifyPassword = lazy(() => import('@admission/pages/ACP/SignInUnifyPassword'));

const UnlockProtectedFeatureACP = lazy(() => import('@admission/pages/ACP/UnlockProtectedFeature'));
const UnlockProtectedFeatureRecoveryPasswordACP = lazy(() => import('@admission/pages/ACP/RecoveryPassword'));
const UnlockProtectedFeatureSuccessACP = lazy(() => import('@admission/pages/ACP/Success/UnlockProtectedFeatureSuccess'));
const UnlockProtectedFeatureInvalidKbaACP = lazy(() => import('@admission/pages/ACP/Error/InvalidResetPasswordKBA'));
const UnlockProtectedFeatureBlockedKbaACP = lazy(() => import('@admission/pages/ACP/Error/BlockedResetPasswordKBA'));
//const ACPSignUpPartnerCompleted = lazy(() => import('@admission/pages/ACP/SignUpPartnerCompleted'));

export function Router() {
  const [alreadyRanFirstPageload, setAlreadyRanFirstPageload] = useState(false);
  const location = useLocation();
  const { track } = useRedline();
  const { titlePage, setCurrentPath } = useTitle();
  const { runDatadogStartView } = useDatadog({ pagePaths, privatePagePaths, publicPagePaths });

  const pagesAdmission = useAdmissionRouter();
  const pagesDues = useDuesRouter();
  const pagesTally = useTallyRouter();
  const pagesHub = useHubRouter();

  // function RedirectToContactUs() {
  //   useEffect(() => {
  //     window.location.href = 'https://www.acordocerto.com.br/fale-conosco';
  //   }, []);

  //   return null;
  // }

  useEffect(() => {
    setCurrentPath(location.pathname);

    if (!alreadyRanFirstPageload) {
      setAlreadyRanFirstPageload(true);
    } else {
      setTimeout(() => {
        track.auto.pageViewed();
      }, 800);
    }
  }, [location]);

  useEffect(() => {
    document.title = `${titlePage} Acordo Certo`;
  }, [titlePage]);

  useEffect(() => {
    runDatadogStartView();
  }, [runDatadogStartView]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Conditional
        condition={import.meta.env.VITE_CUSTOM_BUILD}
        renderIf={
          <Routes>
            <Route element={<PrivateRoutes />}>{pagesDues}</Route>
            <Route path="/*" element={<NotFound />} />
          </Routes>
        }
        renderElse={
          <Routes>
            <Route element={<PublicRoutes />}>{pagesAdmission}</Route>

            <Route element={<PrivateRoutes />}>
              {pagesDues}
              {pagesTally}
              {pagesHub}
              <Route path={pageNames.account.path} element={<Account />} />
              <Route path={pageNames.accountDeletion.path} element={<DeleteAccountOngoing />} />

              <Route path={pageNames.signinUnifyPasswordACP.path} element={<ACPSignInUnifyPassword />} />
              <Route path={pageNames.updatePhoneACP.path} element={<UpdatePhoneACP />} />
              <Route path={pageNames.updateEmailACP.path} element={<UpdateEmailACP />} />
              <Route path={pageNames.updatePasswordACP.path} element={<UpdatePasswordACP />} />
              <Route path={pageNames.updateSocialName.path} element={<UpdateSocialNameACP />} />
              <Route path={pageNames.unlockProtectedFeature.path} element={<UnlockProtectedFeatureACP />} />
              <Route path={pageNames.unlockProtectedFeatureSuccess.path} element={<UnlockProtectedFeatureSuccessACP />} />
              <Route path={pageNames.unlockProtectedFeaturePassword.path} element={<UnlockProtectedFeatureRecoveryPasswordACP />} />
              <Route path={pageNames.unlockProtectedFeatureACPInvalidKBA.path} element={<UnlockProtectedFeatureInvalidKbaACP />} />
              <Route path={pageNames.unlockProtectedFeatureACPBlockedKBA.path} element={<UnlockProtectedFeatureBlockedKbaACP />} />

              <Route path={pageNames.updateContactSuccess.path} element={<UpdateContactSuccessACP />} />
              <Route path={pageNames.updatePasswordSuccess.path} element={<UpdatePasswordSuccessACP />} />
            </Route>
            <Route path="/*" element={<NotFound />} />
            {/* <Route path="/fale-conosco" element={<RedirectToContactUs />} /> */}
          </Routes>
        }
      />
    </Suspense>
  );
}
